import { useCallback } from 'react';

import { createEmptyCondition, createEmptyConditionGroup } from './types';
import type { ConditionItem, LogicalOperator } from './types';

/**
 * Shared CRUD for a root-level `ConditionItem[]` list — used by both Catalog Rules'
 * `RuleCard` (one list per rule, alongside actions/chrome) and the catalog upload
 * template wizard's condition-only `ConditionBuilder`.
 */
export function useConditionList(conditions: ConditionItem[], onChange: (conditions: ConditionItem[]) => void) {
  const handleItemChange = useCallback(
    (itemId: string, updates: Partial<ConditionItem>) => {
      onChange(conditions.map(item => (item.id === itemId ? { ...item, ...updates } as ConditionItem : item)));
    },
    [conditions, onChange],
  );

  const handleItemDelete = useCallback(
    (itemId: string) => {
      onChange(conditions.filter(item => item.id !== itemId));
    },
    [conditions, onChange],
  );

  const handleTopLevelOperatorChange = useCallback(
    (newOperator: LogicalOperator) => {
      onChange(conditions.map(item => ({ ...item, logicalOperator: newOperator })));
    },
    [conditions, onChange],
  );

  const handleAddRule = useCallback(() => {
    onChange([...conditions, createEmptyCondition()]);
  }, [conditions, onChange]);

  const handleAddGroup = useCallback(() => {
    onChange([...conditions, createEmptyConditionGroup()]);
  }, [conditions, onChange]);

  return { handleItemChange, handleItemDelete, handleTopLevelOperatorChange, handleAddRule, handleAddGroup };
}
